import { applySupplierInventoryDeltasToParts } from "./supplierInventoryDeltaUtils";
import { SupplierPart } from "./partsCalculationUtils";
import {
  fetchSupplierPartsInventoryFromTypesense,
  SupplierInventoryPartHit,
} from "./typesenseUtils";

/**
 * Builds rootPartNumber → effective stock for order allocation punch:
 *   Typesense family qty − sold (self-consumption delta) = max(0, TS − sold).
 *
 * Used only for `inventoryItems.availableQuantity` / `inventoryAvailableByRootPart`.
 * Does NOT subtract part-confirmation pending/confirmed — that stays on the
 * discovery / PC request path and must not change what we punch on the order.
 * Missing → 0.
 */
export const buildInventoryAvailableByRootPartFromTypesense = async ({
  typesenseAdapter,
  collectionName,
  supplierId,
  rootPartNumbers,
  supplierInventoryDeltaQuantityMap,
}: {
  typesenseAdapter: any;
  collectionName?: string;
  supplierId: string;
  rootPartNumbers: string[];
  /** Sold / self-consumption only (`delta.quantity`). */
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
}): Promise<Record<string, number>> => {
  const uniqueRoots = Array.from(
    new Set(
      (rootPartNumbers || [])
        .map((root) => String(root || "").trim())
        .filter(Boolean),
    ),
  );

  const inventoryByRoot: Record<string, number> = {};
  for (const root of uniqueRoots) {
    inventoryByRoot[root] = 0;
  }

  if (!supplierId || uniqueRoots.length === 0) {
    return inventoryByRoot;
  }

  const typesenseParts: SupplierInventoryPartHit[] =
    await fetchSupplierPartsInventoryFromTypesense({
      typesenseAdapter,
      collectionName,
      supplierId,
      rootPartNumbers: uniqueRoots,
    });

  const partsForDelta: SupplierPart[] = typesenseParts.map((part) => ({
    rootPartNumber: part.rootPartNumber,
    familyQuantity: part.familyQuantity,
    updatedAt: part.updatedAt,
    source: part.source,
  }));

  // Effective for allocation punch: TS − sold only.
  const deltaAdjustedParts = applySupplierInventoryDeltasToParts({
    supplierId,
    parts: partsForDelta,
    supplierInventoryDeltaQuantityMap,
  });

  for (const part of deltaAdjustedParts) {
    if (!part.rootPartNumber) continue;
    inventoryByRoot[part.rootPartNumber] = Math.max(
      0,
      Number(part.familyQuantity) || 0,
    );
  }

  return inventoryByRoot;
};
